<template>
    <div class="main-container">
        <v-container fluid class="publish-panel overflow-auto pa-5">
            <h4 class="mb-2">{{ $t('publish_event') }}</h4>
            <p class="tgk-paragraph-text" v-html="$t('publish_staff_infotxt')"></p>

            <!-- info box -->
            <SmallInfoBox :event="record" />

            <v-container v-if="['draft', 'rejected'].indexOf(record.status) >= 0" fluid class="px-0">
                <div class="terms-row my-2">
                    <p
                        class="tgk-paragraph-text text-right align-center ma-0"
                        v-html="
                            $t('agree_usage_rights', {
                                usage_rights_url: getUsageRightsUrl(),
                                privacy_policy_url: getPrivacyPolicyUrl(),
                            })
                        "
                    ></p>
                </div>
                <div class="terms-row my-2">
                    <p
                        class="tgk-paragraph-text text-right flex-column align-center ma-0"
                        v-html="$t('agree_data_policy', { data_policy_url: getDataPolicyUrl() })"
                    ></p>
                </div>
                <div class="terms-row my-2">
                    <p
                        class="tgk-paragraph-text text-right align-center ma-0"
                        v-html="$t('agree_term', { terms_policy_url: getTermsPolicyUrl() })"
                    ></p>
                </div>
                <div class="terms-row my-2 align-center">
                    <p
                        class="tgk-paragraph-text text-right align-center my-0 mr-2"
                        v-html="$t('accept_conditions_above')"
                    ></p>
                    <v-checkbox v-model="conditionsAgreement" hide-details dense class="shrink ma-0 pa-0" />
                </div>
            </v-container>

            <v-row justify="end" class="mx-0 mt-5" style="gap: 10px">
                <ActionBtn :disabled="!draftAllowed" @click="onDraftBtn">
                    <KpIcon icon-cls="pencil" color="white" />
                    <span>{{ $t('draft') }}</span>
                </ActionBtn>
                <ActionBtn v-if="userIsStaff" :disabled="!rejectAllowed" @click="onRejectBtn">
                    <KpIcon icon-cls="rejected" color="white" />
                    <span>{{ $t('reject') }}</span>
                </ActionBtn>
                <ActionBtn :disabled="!publishAllowed" @click="onPublishBtn">
                    <KpIcon icon-cls="published" color="white" />
                    <span>{{ $t('publish') }}</span>
                </ActionBtn>
            </v-row>
        </v-container>

        <!-- Side panel -->
        <div class="info-panel overflow-auto pa-5">
            <KpAlert v-for="banner in infoboxesPublish" :key="banner.id" type="info" style="max-width: 600px">
                <h4>{{ banner.title }}</h4>
                <p>{{ banner.comment }}</p>
            </KpAlert>
        </div>
        <ConfirmDialog
            v-model="showPublishConfirmDlg"
            :title="$t('publish')"
            :message="$t('publish_sure')"
            @confirm="doPublish"
        />
        <PromptDialog
            v-if="showRejectConfirmDlg"
            v-model="showRejectConfirmDlg"
            :title="$t('reject')"
            :message="$t('reject_event_sure')"
            :response-required="false"
            :confirm-label="$t('reject').toLowerCase()"
            :confirm-alternate-label="$t('reject_with_mail')"
            @confirm="doReject"
            @confirm-alternate="doRejectWithMail"
        />
        <PromptDialog
            v-if="showDraftConfirmDlg"
            v-model="showDraftConfirmDlg"
            :title="$t('draft')"
            :message="$t('draft_sure')"
            :response-required="false"
            :confirm-label="$t('draft')"
            :confirm-alternate-label="$t('draft_with_mail')"
            @confirm="doDraft"
            @confirm-alternate="doDraftWithMail"
        />
        <EventCheckDialog v-model="showReasonDlg" :reasons="eventCheckReasons" @publish="publishAnyway" />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { Event } from '@/models/Event';
import { instance as api } from '@/helpers/api';
import { formRules, errToStr } from '@/helpers/tools';
import { globalParam } from '@/helpers/globals';
import { userIsStaff } from '@/helpers/auth';

import SmallInfoBox from '@/components/event/SmallInfoBox.vue';
import EventCheckDialog from '@/components/event/EventCheckDialog.vue';
import BackRouteMixin from '@/mixins/backroute';

export default {
    name: 'PublishByEoPanel',
    components: { SmallInfoBox, EventCheckDialog },
    mixins: [BackRouteMixin],

    props: {
        record: {
            type: Event,
            required: true,
        },
        // used to indicate form validity
        value: Boolean,
    },

    data() {
        return {
            conditionsAgreement: false,
            showPublishConfirmDlg: false,
            showRejectConfirmDlg: false,
            showDraftConfirmDlg: false,
            showReasonDlg: false,
            eventCheckReasons: [],
        };
    },

    computed: {
        userIsStaff,
        draftAllowed() {
            return this.record.status !== Event.STATUS_DRAFT;
        },
        rejectAllowed() {
            return this.record.status !== Event.STATUS_REJECTED;
        },
        publishAllowed() {
            return this.record.status !== Event.STATUS_RELEASED && this.conditionsAgreement;
        },
        infoboxesPublish() {
            return this.$store.state.banner.eventInfoboxes.collection
                ? this.$store.state.banner.eventInfoboxes.collection.models.filter(
                      (banner) => banner.type === 'infobox_event_publish',
                  )
                : [];
        },
        hidePreview() {
            return globalParam('event.hide_preview');
        },
    },

    created() {
        this.rules = formRules;
        this.loadEventInfoboxes();
    },

    methods: {
        ...mapActions('banner', ['loadEventInfoboxes']),

        onInput(field, value) {
            this.record.set(field, value);
        },

        getUrl(preview = false) {
            if (preview) {
                return this.record?.detailPreviewUrl;
            }
            return this.record?.detailUrl;
        },

        async onPublishBtn() {
            this.eventCheckReasons = [];
            let reasons = await this.checkEvent(Event.STATUS_RELEASED);
            if (reasons === false) {
                return;
            }
            if (reasons?.length === 0) {
                this.showPublishConfirmDlg = true;
            } else {
                // show reason dialog
                this.eventCheckReasons = reasons;
                this.showReasonDlg = true;
            }
        },

        getUsageRightsUrl() {
            return globalParam('usage_rights_url');
        },
        getPrivacyPolicyUrl() {
            return globalParam('privacy_policy_url');
        },
        getDataPolicyUrl() {
            return globalParam('data_policy_url');
        },
        getTermsPolicyUrl() {
            return globalParam('terms_conditions_url');
        },

        /**
         * This method can be called when the event check was not successful,
         * but the (staff) user would publish the event anyway.
         * It initiates the publish method, even if the checks failed.
         */
        publishAnyway() {
            this.showReasonDlg = false;
            if (this.userIsStaff) {
                this.showPublishConfirmDlg = true;
            }
        },

        /**
         * Initiates a check if the event is ready to be published:
         * the backend checks the record for completeness, and returns
         * a list of errors if not.
         */
        async checkEvent(status) {
            try {
                this.$emit('saving', true);
                return this.record.checkStatusChange(status);
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
                return false;
            } finally {
                this.$emit('saving', false);
            }
        },

        async doPublish() {
            await this.setStatus(
                Event.STATUS_RELEASED,
                true,
                null,
                [this.conditionsAgreement ? 'conditionsAgreement' : false].filter(Boolean),
            );
            this.$toast({ message: this.$t('event_released'), color: 'success' });

            // route back to wher we came from:
            if (this.getBackRouteConfig()?.route) {
                this.$router.push(this.getBackRouteConfig().route);
            } else {
                this.$router.push({ name: 'event' });
            }
        },

        onRejectBtn() {
            this.showRejectConfirmDlg = true;
        },

        async doReject() {
            await this.setStatus(Event.STATUS_REJECTED, false);
            this.$toast({ message: this.$t('event_rejected'), color: 'success' });
        },

        async doRejectWithMail(message) {
            await this.setStatus(Event.STATUS_REJECTED, true, message);
            this.$toast({ message: this.$t('event_rejected_with_mail'), color: 'success' });
        },

        onDraftBtn() {
            this.showDraftConfirmDlg = true;
        },

        async doDraft() {
            await this.setStatus(Event.STATUS_DRAFT);
            this.$toast({ message: this.$t('event_draft'), color: 'success' });
        },
        async doDraftWithMail(message) {
            await this.setStatus(Event.STATUS_DRAFT, true, message);
            this.$toast({ message: this.$t('event_drafted_with_mail'), color: 'success' });
        },

        async setStatus(newStatus, sendMail = true, message = null, legalAcceptances = null) {
            try {
                this.$emit('saving', true);
                await api().post('/Event/status', {
                    event_id: this.record.id,
                    status: newStatus,
                    sendMail,
                    message,
                    legalAcceptances,
                });
                this.$emit('reload-event');
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.$emit('saving', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    grid-template-areas: 'edit info';
    grid-template-rows: auto;
    grid-template-columns: minmax(400px, 800px) minmax(300px, 1fr);
    overflow: auto;
}

.publish-panel {
    grid-area: edit;
}

.info-panel {
    grid-area: info;
    background-color: var(--toolbar-bg-color);
}

.terms-row {
    display: flex;
    justify-content: end;
    flex-wrap: nowrap;
}
</style>
